import React, { useState, useEffect, useRef } from "react";
import { User, X, Plus, Trash2 } from "lucide-react";
import { LineupData } from "../../CreateQickEventV2/types";
import ImageCropper from "../../../../components/common/ImageCropper/ImageCropper";

interface NewLineupModalV3Props {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (lineup: LineupData) => void;
  editingLineup?: LineupData | null;
  isLoading?: boolean;
}

const SOCIAL_PLATFORMS = [
  { value: "instagram", label: "Instagram" },
  { value: "spotify", label: "Spotify" },
  { value: "youtube", label: "YouTube" },
  { value: "twitter", label: "X / Twitter" },
  { value: "facebook", label: "Facebook" },
  { value: "apple_music", label: "Apple Music" },
];

const NewLineupModalV3: React.FC<NewLineupModalV3Props> = ({
  isOpen,
  toggle,
  onSubmit,
  editingLineup = null,
  isLoading = false,
}) => {
  const [name, setName] = useState("");
  const [tagLine, setTagLine] = useState("");
  const [description, setDescription] = useState("");
  const [socialMedia, setSocialMedia] = useState<{ platform: string; url: string }[]>([
    { platform: "instagram", url: "" },
  ]);

  const [artistImage, setArtistImage] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [imageError, setImageError] = useState("");
  const previewUrlRef = useRef<string | null>(null);

  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [fileToCrop, setFileToCrop] = useState<File | null>(null);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (isOpen) {
      if (editingLineup) {
        setName(editingLineup.name || "");
        setTagLine(editingLineup.tagLine || "");
        setDescription(editingLineup.description || "");
        setSocialMedia(
          editingLineup.socialMedia && editingLineup.socialMedia.length > 0
            ? editingLineup.socialMedia
            : [{ platform: "instagram", url: "" }]
        );

        if (editingLineup.artistImage instanceof File) {
          const url = URL.createObjectURL(editingLineup.artistImage);
          previewUrlRef.current = url;
          setPreviewImage(url);
          setArtistImage(editingLineup.artistImage);
        } else if (editingLineup.artistImageUrl) {
          setPreviewImage(editingLineup.artistImageUrl);
        } else {
          setPreviewImage(null);
          setArtistImage(null);
        }
      } else {
        setName("");
        setTagLine("");
        setDescription("");
        setSocialMedia([{ platform: "instagram", url: "" }]);
        setArtistImage(null);
        setPreviewImage(null);
      }
      setValidationError("");
      setImageError("");
      setIsCropperOpen(false);
    }
  }, [editingLineup, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setValidationError("Artist / Performer Name is required.");
      return;
    }

    const lineupItem: LineupData = {
      id: editingLineup?.id || `lineup_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      name: name.trim(),
      tagLine: tagLine.trim(),
      description: description.trim(),
      artistImage,
      artistImageKey: artistImage?.name || editingLineup?.artistImageKey || "",
      artistImageUrl: previewImage,
      sortOrder: editingLineup?.sortOrder || 0,
      isActive: editingLineup?.isActive ?? true,
      socialMedia: socialMedia.filter((item) => item.platform && item.url.trim()),
    };

    onSubmit(lineupItem);
    toggle();
  };

  const handleAddSocial = () => {
    if (socialMedia.length < 6) {
      setSocialMedia([...socialMedia, { platform: "instagram", url: "" }]);
    }
  };

  const handleRemoveSocial = (index: number) => {
    setSocialMedia(socialMedia.filter((_, i) => i !== index));
  };

  const handleSocialChange = (index: number, field: "platform" | "url", val: string) => {
    const updated = [...socialMedia];
    updated[index][field] = val;
    setSocialMedia(updated);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setImageError("");
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setImageError("Max file size is 2 MB.");
      return;
    }

    setFileToCrop(file);
    setIsCropperOpen(true);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 9999,
        background: "rgba(0, 0, 0, 0.75)",
        backdropFilter: "blur(12px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={toggle}
    >
      <div
        className="glass-panel"
        style={{
          width: "100%",
          maxWidth: 560,
          background: "rgba(18, 20, 29, 0.94)",
          border: "1px solid rgba(255, 255, 255, 0.12)",
          borderRadius: 22,
          padding: 24,
          boxShadow: "0 24px 60px rgba(0,0,0,0.8)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255,255,255,0.08)", paddingBottom: 16, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <User size={18} color="#EE3F7C" />
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#fff" }}>
              {editingLineup ? "Edit Artist / Speaker" : "Add New Artist / Speaker"}
            </h3>
          </div>
          <button
            type="button"
            onClick={toggle}
            style={{ background: "none", border: "none", color: "rgba(255,255,255,0.5)", cursor: "pointer", padding: 4 }}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Validation Alert */}
          {validationError && (
            <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(255,93,115,0.15)", border: "1px solid #FF5D73", color: "#FF5D73", fontSize: 12, fontWeight: 600 }}>
              {validationError}
            </div>
          )}

          {/* Artist Name */}
          <div>
            <label className="v3-label">
              Artist / Speaker Name <span style={{ color: "#EE3F7C" }}>*</span>
            </label>
            <input
              type="text"
              className="v3-input"
              placeholder="e.g. Dr. Alex Vance / DJ Kygo"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (validationError) setValidationError("");
              }}
            />
          </div>

          {/* Tag Line / Role */}
          <div>
            <label className="v3-label">Tag Line / Role</label>
            <input
              type="text"
              className="v3-input"
              placeholder="e.g. Grammy Award Winner / Keynote Speaker"
              value={tagLine}
              onChange={(e) => setTagLine(e.target.value)}
            />
          </div>

          {/* Bio / Description */}
          <div>
            <label className="v3-label">Biography / Description</label>
            <textarea
              className="v3-input"
              rows={3}
              placeholder="Tell attendees more about this performer or speaker..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Social Media Links */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <label className="v3-label" style={{ margin: 0 }}>Social Media Links</label>
              {socialMedia.length < 6 && (
                <button
                  type="button"
                  onClick={handleAddSocial}
                  style={{ background: "none", border: "none", color: "#EE3F7C", fontSize: 11, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
                >
                  <Plus size={12} /> Add Link
                </button>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {socialMedia.map((item, idx) => (
                <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <select
                    className="v3-select"
                    style={{ width: 130 }}
                    value={item.platform}
                    onChange={(e) => handleSocialChange(idx, "platform", e.target.value)}
                  >
                    {SOCIAL_PLATFORMS.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                  <input
                    type="url"
                    className="v3-input"
                    placeholder="https://..."
                    value={item.url}
                    onChange={(e) => handleSocialChange(idx, "url", e.target.value)}
                  />
                  {socialMedia.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveSocial(idx)}
                      style={{ background: "rgba(255,93,115,0.15)", border: "1px solid rgba(255,93,115,0.3)", color: "#FF5D73", borderRadius: 8, padding: 8, cursor: "pointer", display: "flex" }}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Artist Image Upload with Cropper */}
          <div>
            <label className="v3-label">Artist Profile Image (Optional)</label>
            {previewImage && (
              <div style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 12 }}>
                <img
                  src={previewImage}
                  alt="Artist Preview"
                  style={{ width: 64, height: 64, borderRadius: 10, objectFit: "cover", border: "1px solid rgba(255,255,255,0.15)" }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setPreviewImage(null);
                    setArtistImage(null);
                  }}
                  style={{ background: "rgba(255,93,115,0.15)", border: "1px solid rgba(255,93,115,0.3)", color: "#FF5D73", borderRadius: 8, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}
                >
                  Remove Image
                </button>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              className="v3-input"
              style={{ fontSize: 11 }}
              onChange={handleFileChange}
            />
            {imageError && <div style={{ fontSize: 11, color: "#FF5D73", marginTop: 4 }}>{imageError}</div>}
          </div>

          {/* Actions */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 16, marginTop: 10 }}>
            <button
              type="button"
              className="v3-btn-secondary"
              onClick={toggle}
              disabled={isLoading}
              style={{ padding: "8px 18px", fontSize: 12 }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="v3-btn-primary"
              disabled={isLoading}
              style={{ padding: "8px 22px", fontSize: 12 }}
            >
              {editingLineup ? "Update Performer" : "Save Performer"}
            </button>
          </div>
        </form>

        {/* Cropper Overlay */}
        {isCropperOpen && fileToCrop && (
          <ImageCropper
            imageFile={fileToCrop}
            aspect={1}
            onCropComplete={(croppedFile) => {
              setArtistImage(croppedFile);
              const url = URL.createObjectURL(croppedFile);
              if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
              previewUrlRef.current = url;
              setPreviewImage(url);
              setIsCropperOpen(false);
            }}
            onCancel={() => setIsCropperOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default NewLineupModalV3;
